import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { TNPSC_UNIT7_QUESTIONS } from "../data/tnpscUnit7Questions";

// ─────────────────────────────────────────────────────────────────────────────
// TNPSC Code 554 — Unit VII: Web Technology. Content is drafted from the
// official syllabus topic list; each concept has an explanation plus a short
// code example. NOT YET human-reviewed by a subject-matter expert — treat
// as a first draft.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["HTML, CSS & JavaScript", "PHP & Java Core", "Java Advanced", "Servlets & MVC", "Node.js & Modern Stacks"];

const SECTION_COLORS = {
  "HTML, CSS & JavaScript": "#2563eb",
  "PHP & Java Core":        "#16a34a",
  "Java Advanced":          "#7c3aed",
  "Servlets & MVC":         "#dc2626",
  "Node.js & Modern Stacks":"#d97706",
};

const CONCEPTS = [
  // ── HTML, CSS & JavaScript ────────────────────────────────────────────────
  { section: "HTML, CSS & JavaScript", title: "Web page designing using HTML",
    explanation: "HTML (HyperText Markup Language) structures web content using nested elements/tags (e.g. <div>, <p>, <table>, <form>). Every HTML document has a <head> (metadata, title) and a <body> (visible content). Attributes (like id, class, href) modify an element's behavior or provide extra information.",
    example: `<!DOCTYPE html>
<html>
<head><title>My Page</title></head>
<body>
  <h1>Welcome</h1>
  <p class="intro">This is a paragraph.</p>
  <a href="https://example.com">Link</a>
</body>
</html>` },
  { section: "HTML, CSS & JavaScript", title: "Scripting basics: client-side vs. server-side",
    explanation: "Client-side scripting (JavaScript) runs IN THE BROWSER after the page loads, handling UI interactivity, form validation, and DOM manipulation without a server round-trip. Server-side scripting (PHP, Java Servlets, Node.js) runs ON THE SERVER, generating dynamic content, accessing databases, and returning a response (often HTML) BEFORE the page reaches the browser.",
    example: `Client-side (runs in browser):  <script>alert("Hi from JS");</script>
Server-side (runs on server):    <?php echo "Hi from PHP"; ?>
Client-side sees the DOM; server-side sees the request/database.` },
  { section: "HTML, CSS & JavaScript", title: "JavaScript objects, literals, operators, and expressions",
    explanation: "JavaScript objects are collections of key-value pairs (properties), created via object literals {} or the new keyword. Operators include arithmetic (+ - * / %), comparison (== vs. the stricter === which also checks type), logical (&& || !), and assignment (= += etc.). An expression evaluates to a value; a statement performs an action.",
    example: `const person = { name: "Anu", age: 21 };  // object literal
console.log(person.name);          // "Anu"
console.log(5 == "5");             // true  (loose, coerces types)
console.log(5 === "5");            // false (strict, checks type too)` },
  { section: "HTML, CSS & JavaScript", title: "JavaScript statements, features, and events",
    explanation: "JavaScript is a dynamically-typed, prototype-based scripting language. Key statements include var/let/const (declaration — let/const are block-scoped, var is function-scoped), if/else, for/while loops, and function declarations. Events (click, load, submit, mouseover) let JS respond to user or browser actions via event listeners/handlers.",
    example: `let count = 0;
document.getElementById("btn").addEventListener("click", function() {
  count++;
  console.log("Clicked " + count + " times");
});` },
  { section: "HTML, CSS & JavaScript", title: "Browser Object Model: window, document, frames",
    explanation: "The Browser Object Model (BOM) exposes browser-level objects to JavaScript: window is the top-level global object (representing the browser tab/window, with methods like alert(), setTimeout()); document represents the loaded HTML page (the DOM root, used for getElementById etc.); frames/iframes let one page embed another HTML document within it.",
    example: `window.alert("Hello");                  // browser window method
document.getElementById("title").innerText = "Updated";  // DOM access
window.location.href = "https://example.com"; // navigate the browser` },
  { section: "HTML, CSS & JavaScript", title: "Verifying forms with JavaScript",
    explanation: "Client-side form validation checks user input (required fields, valid email format, numeric ranges) BEFORE submission, giving instant feedback without a server round-trip — but it must never be the ONLY validation, since client-side JS can be bypassed; the server must always re-validate.",
    example: `function validateForm() {
  const email = document.forms["myForm"]["email"].value;
  if (!email.includes("@")) {
    alert("Enter a valid email");
    return false;   // blocks form submission
  }
  return true;
}
// <form onsubmit="return validateForm()">` },
  { section: "HTML, CSS & JavaScript", title: "HTML5 and CSS3",
    explanation: "HTML5 introduced semantic elements (<header>, <nav>, <article>, <footer>), native multimedia (<audio>, <video>), the <canvas> element, and new form input types (email, date, range). CSS3 introduced modules like Flexbox and Grid for layout, transitions/animations, media queries for responsive design, and rounded corners/shadows without images.",
    example: `<header>Site Header</header>
<video controls src="movie.mp4"></video>
<style>
  .box { display: flex; border-radius: 8px; transition: all 0.3s ease; }
  @media (max-width: 600px) { .box { flex-direction: column; } }
</style>` },
  { section: "HTML, CSS & JavaScript", title: "HTML5 canvas",
    explanation: "The <canvas> element provides a scriptable, pixel-based drawing surface. JavaScript accesses its 2D (or WebGL) rendering context via getContext('2d') and draws shapes, images, and text imperatively, frame by frame — commonly used for graphs, games, and image manipulation.",
    example: `<canvas id="c" width="200" height="100"></canvas>
<script>
  const ctx = document.getElementById("c").getContext("2d");
  ctx.fillStyle = "blue";
  ctx.fillRect(10, 10, 80, 50);   // draws a blue rectangle
</script>` },
  { section: "HTML, CSS & JavaScript", title: "Event handling in JavaScript",
    explanation: "Event handling can be done via inline HTML attributes (onclick=\"...\"), DOM property assignment (element.onclick = fn), or the modern addEventListener (allows multiple listeners on the same element/event, and supports capturing/bubbling phase control). Event bubbling propagates an event from the target element UP through its ancestors unless stopPropagation() is called.",
    example: `el.addEventListener("click", handler1);
el.addEventListener("click", handler2);   // BOTH run — unlike el.onclick = ...
                                           // which only keeps the last assignment
event.stopPropagation();  // prevents the event bubbling to parent elements` },

  // ── PHP & Java Core ────────────────────────────────────────────────────────
  { section: "PHP & Java Core", title: "PHP scripting basics",
    explanation: "PHP is a server-side scripting language embedded in HTML using <?php ... ?> tags, executed on the server before the page is sent to the browser. Variables start with $ and are loosely typed. Superglobals like $_GET, $_POST, and $_SESSION access request data and session state.",
    example: `<?php
  $name = $_POST['name'];
  echo "Hello, " . htmlspecialchars($name);
?>
<!-- runs on the server; browser only ever sees the resulting HTML output -->` },
  { section: "PHP & Java Core", title: "Features of Java",
    explanation: "Java is platform-independent ('write once, run anywhere' via the JVM and bytecode), object-oriented, strongly typed (compile-time type checking), automatically memory-managed (garbage collection — no manual free()), and includes built-in support for multithreading and networking.",
    example: `Key features: Platform-independent (JVM) · Object-oriented · Strongly typed
              Automatic garbage collection · Multithreaded · Secure (sandboxed)
"Write Once, Run Anywhere" — same .class bytecode runs on any JVM.` },
  { section: "PHP & Java Core", title: "Java data types, variables, and arrays",
    explanation: "Java has 8 primitive types: byte, short, int, long, float, double, char, boolean — these are NOT objects and have fixed sizes. Arrays are objects with a fixed length set at creation, indexed from 0, and declared as type[] name or type name[]. Java is statically typed — a variable's type is fixed at declaration and checked at compile time.",
    example: `int[] nums = {10, 20, 30};
System.out.println(nums.length);    // 3 — arrays have a fixed .length field
int x;                              // declared, must be assigned before use
boolean flag = true;                // primitive, not an object` },
  { section: "PHP & Java Core", title: "Java operators and control statements",
    explanation: "Java operators mirror C/C++: arithmetic, relational, logical (&&, ||, ! — short-circuiting), bitwise, and the ternary ?:. Control statements include if/else, switch (supports String since Java 7), for, while, do-while, and the enhanced for-each loop for iterating collections/arrays.",
    example: `for (int n : nums) {          // for-each ("enhanced for") loop
    System.out.println(n);
}
switch (day) {                // Java 7+ allows switch on String
    case "MON": System.out.println("Monday"); break;
}` },
  { section: "PHP & Java Core", title: "Classes and methods in Java",
    explanation: "A Java class is a blueprint containing fields (state) and methods (behavior). Method overloading allows multiple methods with the SAME name but DIFFERENT parameter lists in one class, resolved at compile time. Constructors share the class's name and have no return type, and are called automatically when an object is created with 'new'.",
    example: `class Calc {
  int add(int a, int b) { return a + b; }
  double add(double a, double b) { return a + b; }  // overloaded
}
Calc c = new Calc();   // constructor call via 'new'` },
  { section: "PHP & Java Core", title: "Inheritance in Java",
    explanation: "Java supports single inheritance of classes (extends keyword) — a class can extend only ONE superclass, avoiding the multiple-inheritance diamond problem, though multiple INTERFACES can be implemented. super refers to the immediate parent class, used to call its constructor or overridden methods. Method overriding lets a subclass redefine a superclass's method, resolved at RUN TIME (dynamic/run-time polymorphism).",
    example: `class Animal { void sound() { System.out.println("..."); } }
class Dog extends Animal {
  @Override
  void sound() { System.out.println("Bark"); }   // run-time polymorphism
}
Animal a = new Dog();
a.sound();  // prints "Bark" — resolved at run time based on actual object` },
  { section: "PHP & Java Core", title: "Packages and interfaces",
    explanation: "A package groups related classes/interfaces under one namespace (e.g. java.util), preventing naming conflicts and controlling access. An interface declares method signatures (a contract) with no implementation (before Java 8; default/static methods are now allowed) — a class 'implements' an interface and MUST provide bodies for all its abstract methods, and CAN implement multiple interfaces (Java's way around multiple inheritance).",
    example: `package com.myapp.utils;
interface Shape { double area(); }         // contract, no implementation
class Circle implements Shape {
  double r;
  public double area() { return Math.PI * r * r; }  // must implement
}` },
  { section: "PHP & Java Core", title: "Exception handling in Java",
    explanation: "Java exceptions are objects thrown when an abnormal condition occurs, handled with try/catch/finally. Checked exceptions (e.g. IOException) MUST be declared (throws) or caught at compile time; unchecked exceptions (RuntimeException and its subclasses, e.g. NullPointerException) are NOT compiler-enforced. finally always executes, whether or not an exception occurred, typically used for cleanup.",
    example: `try {
    int[] a = new int[3];
    System.out.println(a[5]);           // throws ArrayIndexOutOfBoundsException
} catch (ArrayIndexOutOfBoundsException e) {
    System.out.println("Caught: " + e.getMessage());
} finally {
    System.out.println("Always runs");  // cleanup code
}` },
  { section: "PHP & Java Core", title: "Multithreaded programming in Java",
    explanation: "Java threads can be created by extending the Thread class or implementing the Runnable interface (preferred, since Java doesn't allow multiple inheritance). synchronized methods/blocks prevent race conditions by allowing only one thread to hold a given object's lock at a time. Thread states include New, Runnable, Blocked/Waiting, and Terminated.",
    example: `class MyTask implements Runnable {
    public void run() { System.out.println("Running in a thread"); }
}
Thread t = new Thread(new MyTask());
t.start();      // schedules the thread — NOT the same as calling run() directly

synchronized void increment() { count++; }   // only one thread at a time` },
  { section: "PHP & Java Core", title: "Java I/O and files",
    explanation: "java.io provides stream-based I/O: byte streams (InputStream/OutputStream, for binary data) and character streams (Reader/Writer, for text, handling character encoding). The File class represents file/directory paths and metadata. try-with-resources (Java 7+) automatically closes any resource implementing AutoCloseable, even if an exception occurs.",
    example: `try (BufferedReader br = new BufferedReader(new FileReader("data.txt"))) {
    String line = br.readLine();
    System.out.println(line);
}  // br.close() called automatically, even on exception` },
  { section: "PHP & Java Core", title: "Utility classes and String handling",
    explanation: "java.util provides utility classes like ArrayList, HashMap, Collections, and Scanner (console/text input). Java Strings are IMMUTABLE — every 'modification' (concat, substring) creates a NEW String object rather than altering the original. StringBuilder/StringBuffer provide a MUTABLE character sequence for efficient repeated concatenation (StringBuffer is synchronized/thread-safe, StringBuilder is not, but faster).",
    example: `String s = "Hello";
s.concat(" World");     // does NOT change s — Strings are immutable!
System.out.println(s);  // still prints "Hello"

StringBuilder sb = new StringBuilder("Hello");
sb.append(" World");    // modifies sb in place — mutable` },

  // ── Java Advanced ───────────────────────────────────────────────────────────
  { section: "Java Advanced", title: "JDBC overview and implementation",
    explanation: "JDBC (Java Database Connectivity) is a standard API for connecting Java applications to relational databases. The typical flow: load the driver, obtain a Connection via DriverManager.getConnection(url, user, pass), create a Statement/PreparedStatement, execute a query, and process the returned ResultSet. PreparedStatement is preferred over Statement since it precompiles the query and prevents SQL injection via parameterized placeholders (?).",
    example: `Connection con = DriverManager.getConnection(url, user, pass);
PreparedStatement ps = con.prepareStatement("SELECT * FROM users WHERE id=?");
ps.setInt(1, 101);
ResultSet rs = ps.executeQuery();
while (rs.next()) { System.out.println(rs.getString("name")); }` },
  { section: "Java Advanced", title: "Networking: InetAddress and URL classes",
    explanation: "java.net.InetAddress represents an IP address and provides host-name-to-IP resolution (InetAddress.getByName()). The URL class parses and represents a Uniform Resource Locator (protocol, host, port, path), and openConnection()/openStream() allow reading content directly from a web resource.",
    example: `InetAddress addr = InetAddress.getByName("www.google.com");
System.out.println(addr.getHostAddress());   // prints resolved IP

URL url = new URL("https://example.com/page");
System.out.println(url.getHost());   // "example.com"` },
  { section: "Java Advanced", title: "TCP and UDP sockets",
    explanation: "TCP sockets (Socket/ServerSocket) provide RELIABLE, connection-oriented, ordered byte-stream communication — used when data integrity matters (file transfer, HTTP). UDP sockets (DatagramSocket/DatagramPacket) provide CONNECTIONLESS, unreliable, unordered packet delivery with lower overhead — used when speed matters more than guaranteed delivery (video streaming, DNS queries).",
    example: `// TCP server
ServerSocket server = new ServerSocket(8080);
Socket client = server.accept();     // blocks until a client connects

// UDP — no connection setup needed
DatagramSocket ds = new DatagramSocket();
DatagramPacket packet = new DatagramPacket(data, data.length, addr, port);
ds.send(packet);` },
  { section: "Java Advanced", title: "JavaBeans",
    explanation: "A JavaBean is a reusable Java class following specific conventions: a public no-argument constructor, private fields accessed via public getter/setter methods (getX()/setX()), and Serializable implementation — enabling visual builder tools and frameworks (like JSP, Spring) to introspect and manipulate bean properties automatically.",
    example: `public class PersonBean implements java.io.Serializable {
    private String name;
    public PersonBean() {}                 // no-arg constructor required
    public String getName() { return name; }
    public void setName(String n) { name = n; }
}` },
  { section: "Java Advanced", title: "RMI (Remote Method Invocation)",
    explanation: "RMI lets a Java object on one JVM invoke methods on an object living in ANOTHER JVM (possibly on a different machine) as if it were local — the remote object's interface must extend java.rmi.Remote, and its methods declare 'throws RemoteException'. A registry (rmiregistry) binds remote object names to their stubs so clients can look them up.",
    example: `interface Hello extends Remote {
    String sayHello() throws RemoteException;
}
// server registers: Naming.rebind("HelloService", helloImpl);
// client looks up:  Hello h = (Hello) Naming.lookup("rmi://host/HelloService");
h.sayHello();  // looks local, actually executes on the remote JVM` },
  { section: "Java Advanced", title: "Java Applets: lifecycle basics",
    explanation: "A Java Applet is a program embedded in a web page and run inside a browser's JVM (largely obsolete today, but a common TNPSC topic). Its lifecycle methods, called automatically by the browser, are: init() (once, on load), start() (each time the page is shown), stop() (when the page is left), and destroy() (once, when the applet is unloaded).",
    example: `public class MyApplet extends Applet {
    public void init()    { /* one-time setup */ }
    public void start()   { /* called each time page becomes visible */ }
    public void stop()    { /* called when leaving the page */ }
    public void destroy() { /* final cleanup */ }
}` },
  { section: "Java Advanced", title: "Applets: images, sound, and parameters",
    explanation: "Applets load images via getImage(getDocumentBase(), \"pic.png\") and draw them with Graphics.drawImage(). Sound clips are loaded via getAudioClip() and played with .play() or .loop(). Parameters are passed from the HTML <applet>/<param> tags and read inside the applet using getParameter(\"name\").",
    example: `<applet code="MyApplet.class" width="200" height="200">
  <param name="username" value="Anu">
</applet>

// inside the applet:
String user = getParameter("username");           // "Anu"
Image img = getImage(getDocumentBase(), "logo.png");
g.drawImage(img, 0, 0, this);` },
  { section: "Java Advanced", title: "AWT: working with windows, graphics, and text",
    explanation: "AWT (Abstract Window Toolkit) is Java's original GUI toolkit, using native (heavyweight) OS widgets. Frame represents a top-level window; the Graphics class (passed to paint(Graphics g)) provides drawing primitives (drawLine, drawRect, drawString, drawOval) for rendering shapes and text directly onto a component.",
    example: `class MyFrame extends Frame {
    public void paint(Graphics g) {
        g.drawString("Hello AWT", 20, 20);
        g.drawRect(10, 10, 100, 50);
    }
}
MyFrame f = new MyFrame();
f.setSize(300, 200);
f.setVisible(true);` },
  { section: "Java Advanced", title: "AWT controls, layout managers, and menus",
    explanation: "AWT controls include Button, Label, TextField, Checkbox, Choice, and List. Layout managers automatically arrange components: FlowLayout (left-to-right, wraps), BorderLayout (North/South/East/West/Center, 5 regions, default for Frame), and GridLayout (equal-sized rows/columns). MenuBar/Menu/MenuItem build a window's top menu bar.",
    example: `Frame f = new Frame();
f.setLayout(new BorderLayout());
f.add(new Button("North"), BorderLayout.NORTH);
f.add(new Button("Center"), BorderLayout.CENTER);

MenuBar mb = new MenuBar();
Menu file = new Menu("File");
file.add(new MenuItem("Exit"));
mb.add(file);
f.setMenuBar(mb);` },

  // ── Servlets & MVC ────────────────────────────────────────────────────────
  { section: "Servlets & MVC", title: "Servlets: lifecycle and the Servlet API",
    explanation: "A Servlet is a Java class running inside a web/servlet container (e.g. Tomcat) that handles HTTP requests. Its lifecycle: init() (called ONCE when the servlet is first loaded), service() (called for EVERY request, dispatching to doGet/doPost based on HTTP method), and destroy() (called once when the servlet is taken out of service). HttpServlet is the common base class extended for web servlets.",
    example: `public class MyServlet extends HttpServlet {
    public void init() { /* one-time setup, e.g. open a DB pool */ }
    protected void doGet(HttpServletRequest req, HttpServletResponse resp) {
        // handles every GET request
    }
    public void destroy() { /* one-time cleanup */ }
}` },
  { section: "Servlets & MVC", title: "Handling HTTP request/response, cookies, and session tracking",
    explanation: "HttpServletRequest exposes request data (parameters via getParameter(), headers, request method); HttpServletResponse builds the reply (setContentType, getWriter() for output). Cookies (small client-stored key-value pairs sent with every request to that domain) and HttpSession (server-side storage tied to a session ID, usually delivered via a cookie) are the two main mechanisms for tracking state across the otherwise-stateless HTTP protocol.",
    example: `String name = request.getParameter("name");   // read form/query data
response.setContentType("text/html");
response.getWriter().println("<h1>Hi " + name + "</h1>");

HttpSession session = request.getSession();    // creates if not present
session.setAttribute("user", name);            // persists across requests` },
  { section: "Servlets & MVC", title: "MVC architecture",
    explanation: "Model-View-Controller separates an application into three roles: the Model (business logic and data, unaware of the UI), the View (presentation — renders data to the user, e.g. a JSP page), and the Controller (a Servlet that receives requests, invokes the Model, and selects which View to render) — improving maintainability by decoupling data, logic, and presentation.",
    example: `Request → Controller (Servlet)
              ↓ calls
          Model (business logic + data)
              ↓ result passed to
          View (JSP renders the final HTML) → Response

// Controller decides: request.getRequestDispatcher("result.jsp").forward(req, res);` },

  // ── Node.js & Modern Stacks ─────────────────────────────────────────────
  { section: "Node.js & Modern Stacks", title: "Node.js: events, listeners, timers, and callbacks",
    explanation: "Node.js runs JavaScript on the server using a single-threaded, EVENT-DRIVEN, non-blocking I/O model built on an event loop. EventEmitter lets code emit named events and register listeners (.on()) that react to them. Timers (setTimeout, setInterval) schedule code to run later. Callbacks are functions passed as arguments to be invoked once an asynchronous operation completes (traditionally with an (error, result) signature).",
    example: `const EventEmitter = require('events');
const emitter = new EventEmitter();
emitter.on('greet', (name) => console.log('Hello ' + name));
emitter.emit('greet', 'Anu');

setTimeout(() => console.log('runs after 1s'), 1000);
fs.readFile('file.txt', (err, data) => { if (!err) console.log(data); });` },
  { section: "Node.js & Modern Stacks", title: "Implementing an HTTP service in Node.js",
    explanation: "Node's built-in http module can create a web server without any framework: http.createServer() takes a callback invoked for every incoming request, receiving (req, res) objects to read the request and write the response. Because Node is non-blocking, one process can handle many concurrent connections efficiently without one thread per connection.",
    example: `const http = require('http');
const server = http.createServer((req, res) => {
    res.writeHead(200, { 'Content-Type': 'text/plain' });
    res.end('Hello from Node.js!');
});
server.listen(3000, () => console.log('Server running on port 3000'));` },
  { section: "Node.js & Modern Stacks", title: "NoSQL and MongoDB",
    explanation: "NoSQL databases store data WITHOUT a fixed relational schema, trading strict ACID consistency (in some designs) for horizontal scalability and flexible data models — categories include document stores (MongoDB), key-value stores (Redis), column-family (Cassandra), and graph databases (Neo4j). MongoDB stores data as BSON (binary JSON) documents grouped into collections (analogous to a SQL table), with no fixed columns required across documents.",
    example: `// MongoDB document (analogous to a SQL row, but schema-flexible)
{ "_id": 1, "name": "Anu", "skills": ["Java", "Node"] }

db.students.find({ name: "Anu" });     // query, similar spirit to SQL SELECT
db.students.insertOne({ name: "Ravi", skills: ["Python"] });` },
  { section: "Node.js & Modern Stacks", title: "Frameworks: Spring",
    explanation: "Spring is a comprehensive Java application framework built around Dependency Injection (the framework, not the code, creates and wires object dependencies, reducing coupling) and Inversion of Control (IoC). Spring Boot simplifies Spring configuration with auto-configuration and an embedded server, enabling quick standalone REST API/microservice development.",
    example: `@RestController
class HelloController {
    @GetMapping("/hello")
    public String hello() { return "Hello, Spring Boot!"; }
}
// Spring's IoC container creates and wires this controller automatically —
// no manual "new HelloController()" needed anywhere in application code.` },
  { section: "Node.js & Modern Stacks", title: "MERN, MEAN, and Flutter",
    explanation: "MERN (MongoDB, Express.js, React, Node.js) and MEAN (MongoDB, Express.js, Angular, Node.js) are full-stack JavaScript frameworks sharing MongoDB/Express/Node, differing in the front-end library (React vs. Angular) — both use JavaScript/JSON end-to-end from browser to database. Flutter (Google) is a cross-platform UI toolkit using the Dart language to compile a SINGLE codebase to natively-performing Android, iOS, web, and desktop apps.",
    example: `MERN stack: MongoDB (DB) + Express (backend framework) + React (frontend) + Node.js
MEAN stack: MongoDB + Express + Angular (frontend) + Node.js
Flutter: one Dart codebase → compiled native apps for Android AND iOS` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function TnpscUnit7Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  const filtered = useMemo(() => (
    activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection)
  ), [activeSection]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? TNPSC_UNIT7_QUESTIONS : TNPSC_UNIT7_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div style={S.page}>
      <Link to="/tnpsc" style={S.back}>← Back to TNPSC 554 Menu</Link>
      {" · "}
      <Link to="/tnpsc-unit6" style={S.back}>← Unit VI</Link>
      {" · "}
      <Link to="/tnpsc-unit8" style={S.back}>Unit VIII →</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🎓 TNPSC 554 — Unit VII</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Web Technology</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          20 questions in the exam · {CONCEPTS.length} concepts explained · {TNPSC_UNIT7_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({TNPSC_UNIT7_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a href={`#concept-${i}`} style={{ color:"#e2e8f0", textDecoration:"none" }}>{c.title}</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / CODE</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
      ))}
      </>
      )}
    </div>
  );
}
